import { type FC, memo, useEffect, useState } from 'react'

import { Table } from 'common/components/Table/Table'
import { DEFAULT_TABLE_PARAMS_WITH_SEARCH } from 'common/constants/defaultTableParamsConstants'

import { TableSearchAndFilters } from 'common/components/TableSearchAndFilters/TableSearchAndFilters'
import type { TableParamsType } from 'common/interfaces/ITable'
import type { ObjectType } from 'common/interfaces/object'
import { useParams } from 'react-router'
import { PrescriptionsTableColumns } from './columns'
import {
  useLazyGetPrescriptionsQuery,
  useExportPrescriptionsMutation,
} from 'features/Profile/features/Prescriptions/patient-data.api'
import styles from './prescriptions.module.scss'
import { Col, Divider, Row, Switch } from 'antd'
import { Button } from 'common/components/Button/Button'
import { BUTTON_PROPORTION } from 'common/constants/buttonConstants'
import { downloadFile } from 'common/services/utilService'

export const Prescriptions: FC = memo(() => {
  const [columns, setColumns] = useState<ObjectType[]>(PrescriptionsTableColumns)
  const [with_pid, setWith_pid] = useState<boolean>(true)
  const [emr_sync_id, setEmr_sync_id] = useState<boolean>(true)
  const [tableParams, setTableParams] = useState<TableParamsType>({
    ...DEFAULT_TABLE_PARAMS_WITH_SEARCH,
  })

  const { siteId } = useParams()

  const [fetchPrescriptions, { data: prescriptionsData, isFetching }] =
    useLazyGetPrescriptionsQuery()

  const [exportPrescriptions, { isLoading: isExporting }] = useExportPrescriptionsMutation()

  useEffect(() => {
    if (tableParams.sorters) {
      setColumns((prev) =>
        prev.map((column) => ({
          ...column,
          sortOrder: tableParams.sorters?.[column.key]?.order,
        })),
      )
    }
  }, [tableParams.sorters])

  useEffect(() => {
    if (siteId) {
      fetchPrescriptions({
        siteId,
        tableParams: {
          ...tableParams,
          search: tableParams.debouncedValue?.length >= 2 ? tableParams.debouncedValue : '',
        },
        with_pid,
        emr_sync_id,
      })
    }
  }, [siteId, tableParams.debouncedValue, tableParams.sorters, with_pid, emr_sync_id])

  useEffect(() => {
    if (Number.isInteger(prescriptionsData?.total_items)) {
      setTableParams((prev) => ({ ...prev, total: prescriptionsData.total_items }))
    }
  }, [prescriptionsData?.total_items])

  const handleDownloadReport = async () => {
    if (!siteId) return

    try {
      const blob = await exportPrescriptions({
        siteId,
        with_pid,
        emr_sync_id,
      }).unwrap()

      const fileName = 'prescriptions_export.csv'
      await downloadFile(blob, 'text/csv', fileName)
    } catch (error) {
      console.error('Failed to download report:', error)
    }
  }

  return (
    <div className={styles.parent}>
      <h1 className='page-title'>Prescriptions</h1>
      <Row justify='space-between' align='top' gutter={[16, 16]}>
        <Col>
          <TableSearchAndFilters
            setTableParams={setTableParams}
            setColumns={setColumns}
            tableParams={tableParams}
            inputHint='Search...'
          />
        </Col>
        <Col>
          <div className={styles.additionalFilters}>
            <div className={styles.additionalFiltersItem}>
              Patients not resolved
              <Switch checked={with_pid} onChange={(checked) => setWith_pid(checked)} /> Patients
              resolved
            </div>
            <Divider type='vertical' style={{ height: '24px' }} />
            <div className={styles.additionalFiltersItem}>
              Not Synced with EMR
              <Switch checked={emr_sync_id} onChange={(checked) => setEmr_sync_id(checked)} />
              Synced with EMR
            </div>
          </div>
        </Col>
      </Row>

      <Table
        itemKey='id'
        haveActions={false}
        tableParams={tableParams}
        setTableParams={setTableParams}
        dataSource={prescriptionsData?.items}
        columns={columns}
        isLoading={isFetching}
        pagination
      />
    </div>
  )
})
